"use client";

import { usePathname } from "next/navigation";
import { useLingui } from "@lingui/react/macro";
import { Trans } from "@lingui/react/macro";
import {
  DropdownMenuPortal,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
} from "@/components/ui/dropdown-menu";
import { DEFAULT_LOCALE, LOCALES, LOCALE_FLAGS, LOCALE_NAMES, isLocale, type Locale } from "@/lib/i18n/locales";
import { switchLocale } from "@/lib/i18n/switch-locale";

/**
 * Locale picker for the signed-in chrome (profile menu), as a submenu.
 *
 * It used to be a segmented control rendering every locale at once, matching
 * ThemeToggle directly above it. That only worked at two: the menu is
 * `min-w-[260px]`, the row spends ~60px on its label, and three full native
 * names do not fit the ~164px left — let alone the next locale. ThemeToggle
 * gets away with three because its options are icons, and language has no
 * honest icon (a flag is a country, not a language: German is not only
 * Germany's, and Swedish is not only Sweden's).
 *
 * A submenu is O(1) in the row regardless of how many locales ship, so adding
 * one is a registry entry and a translation — never another redesign. It also
 * states the current language by name instead of leaving the reader to infer
 * it from which pill is highlighted.
 *
 * Must be rendered inside a DropdownMenu (it is a Radix submenu).
 */
export function LanguageToggle() {
  const { t, i18n } = useLingui();
  const pathname = usePathname();
  const current: Locale = isLocale(i18n.locale) ? i18n.locale : DEFAULT_LOCALE;

  return (
    <DropdownMenuSub>
      <DropdownMenuSubTrigger className="px-3 py-2.5 rounded-none border-b border-border-subtle">
        <span className="text-sm text-foreground">
          <Trans>Language</Trans>
        </span>
        {/* The current language, in its own language. The flag is decoration
            beside it and is hidden from assistive tech — see LOCALE_FLAGS. */}
        <span className="ml-auto flex items-center gap-1.5 text-sm text-foreground-muted">
          <span aria-hidden="true">{LOCALE_FLAGS[current]}</span>
          {LOCALE_NAMES[current]}
        </span>
      </DropdownMenuSubTrigger>
      <DropdownMenuPortal>
        <DropdownMenuSubContent aria-label={t`Language`}>
          <DropdownMenuRadioGroup
            value={current}
            onValueChange={(next) => {
              // Guard the no-op: switchLocale navigates, and re-picking the
              // language you are already in should not reload the page.
              if (isLocale(next) && next !== current) switchLocale(next, pathname);
            }}
          >
            {LOCALES.map((locale) => (
              <DropdownMenuRadioItem key={locale} value={locale}>
                <span aria-hidden="true" className="mr-2">{LOCALE_FLAGS[locale]}</span>
                {LOCALE_NAMES[locale]}
              </DropdownMenuRadioItem>
            ))}
          </DropdownMenuRadioGroup>
        </DropdownMenuSubContent>
      </DropdownMenuPortal>
    </DropdownMenuSub>
  );
}
